/**
 * Handles changes and updates of tariffs to decide whether to show
 * or hide a widget according to the conditions listed in the `CONDITIONS` hash
 * The element to be shown/hidden declares which condition to use through the `data-condition`
 * and can possibly negate having the `data-condition-negate="true"`
 * @module embed/conditional_widget
 */

const SELECTOR = '.js-conditional-widget';
const ATTR_CONDITION = 'data-condition';
const ATTR_CONDITION_NEGATE = 'data-condition-negate';
const ATTR_FUEL_TYPE = 'data-fuel-type';
const ATTR_ESTIMATION_TYPE = 'data-estimation-type';

import { show, hide } from '@/lib/show_hide';
import {
  hasGas,
  hasDualFuel,
  isActualUsageInput,
  hasPostcode,
  hasEconomy7,
  hasSameBillingAddress,
  getEstimationType,
  hasTariffEndDate,
  hasMedicalCondition,
  hasSmartMeter,
  hasCoOperativeMembershipNumber,
  getSupplier,
  getElectricityUsage,
  getGasUsage
} from './form';

const CONDITIONS = {
  hasGasAndElectricity(scope) {
    return hasGas(scope);
  },
  isNeedingGasFields(scope) {
    return hasGas(scope) && !hasDualFuel(scope);
  },
  isNeedingUsageInput(scope, el) {
    const fuelType = el.getAttribute(ATTR_FUEL_TYPE);
    if (fuelType == 'gas') {
      return hasGas(scope) && isActualUsageInput(scope, fuelType);
    } else {
      return isActualUsageInput(scope, fuelType);
    }
  },
  hasTariffEndDate(scope, el) {
    const fuelType = el.getAttribute(ATTR_FUEL_TYPE);
    return (
      CONDITIONS.isTariffListAvailable(scope, el) &&
      hasTariffEndDate(scope, fuelType)
    );
  },
  isNeedingUsageInputOfType(scope, el) {
    const fuelType = el.getAttribute(ATTR_FUEL_TYPE);
    const estimationType = el.getAttribute(ATTR_ESTIMATION_TYPE);
    return getEstimationType(scope, fuelType) == estimationType;
  },
  hasEconomy7,
  hasDistinctBillingAddress: scope => !hasSameBillingAddress(scope),
  hasMedicalCondition: scope => hasMedicalCondition(scope),
  hasSmartMeter: scope => hasSmartMeter(scope),
  hasCoOperativeMembershipNumber: scope =>
    hasCoOperativeMembershipNumber(scope),
  isTariffListAvailable(scope, el) {
    const fuelType = el.getAttribute(ATTR_FUEL_TYPE);
    const wouldShow = !!hasPostcode(scope) && !!getSupplier(scope, fuelType);
    if (fuelType == 'gas') {
      return CONDITIONS.isNeedingGasFields(scope) && wouldShow;
    } else {
      return wouldShow;
    }
  },
  hasHigherElectricityConsumption(scope) {
    const electricityUsage = getElectricityUsage(scope, { annual: true });
    const gasUsage = getGasUsage(scope, { annual: true });
    // If either is not filled
    if (
      electricityUsage &&
      electricityUsage != 0 &&
      gasUsage &&
      gasUsage != 0
    ) {
      return electricityUsage > gasUsage;
    }
    return false;
  }
};

function satisfiesCondition(condition, { scope, el, negate }) {
  return negate ? !condition(scope, el) : condition(scope, el);
}

/**
 * @param {HTMLElement} scope The wrapper on which to listen on the events
 * @param {HTMLElement} el The event that will be shown/hidden
 */
function setupConditionalWidget(scope, el) {
  function checkCondition() {
    const condition = CONDITIONS[el.getAttribute(ATTR_CONDITION)];
    const negate = el.getAttribute(ATTR_CONDITION_NEGATE);
    if (condition && !satisfiesCondition(condition, { scope, el, negate })) {
      hide(el, { disable: true });
      // Disable form controls so their values don't get sent
    } else {
      if (el.classList.contains('d-none')) {
        dispatchWillShow(el);
      }
      show(el, { enable: true });
    }
  }

  scope.addEventListener('change', checkCondition);
  scope.addEventListener('didSetupTariff', checkCondition);
  checkCondition();
}

export default function setupConditionalWidgets(el) {
  Array.from(el.querySelectorAll(SELECTOR)).forEach(widget =>
    setupConditionalWidget(el, widget)
  );
}

function dispatchWillShow(el) {
  const event = new CustomEvent('willShow', { bubbles: true });
  el.dispatchEvent(event);
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/embed/conditional_widget.js